import React, { useState, useEffect } from 'react';
import { ShieldAlert, Plus, Search, Trash2, Edit, AlertCircle, Bot } from 'lucide-react';
import axios from 'axios';

export default function SensitiveWords() {
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Brand selection
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(localStorage.getItem('globalBrand') || '');
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWord, setEditingWord] = useState(null);
  const [formData, setFormData] = useState({ word: '', category: '负面评价', enabled: true, llmCheck: true });

  const categories = ['负面评价', '安全隐患', '售后投诉', '虚假宣传', '竞品对比', '其他'];

  const fetchBrandsAndWords = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const brandRes = await axios.get('/api/brand-projects?system=geo', { headers: { 'Authorization': `Bearer ${token}` } });
      const fetchedBrands = brandRes.data.data || [];
      setBrands(fetchedBrands);
      
      let projectId = selectedBrand;
      if (!projectId || projectId === 'all' || projectId === 'undefined') {
        if (fetchedBrands.length > 0) {
          projectId = fetchedBrands[0].id.toString();
          setSelectedBrand(projectId);
        } else {
          setWords([]);
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      const res = await axios.get(`/api/brand-projects/${projectId}/sensitive-words`);
      setWords(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch data', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrandsAndWords();
    
    const handleGlobalChange = () => {
       const globalBrand = localStorage.getItem('globalBrand');
       if (globalBrand && globalBrand !== 'all') {
         setSelectedBrand(globalBrand);
       }
    };
    window.addEventListener('globalFilterChange', handleGlobalChange);
    return () => window.removeEventListener('globalFilterChange', handleGlobalChange);
  }, []);
  
  useEffect(() => {
    if (selectedBrand && selectedBrand !== 'all') {
      const fetchWordsOnly = async () => {
        setLoading(true);
        try {
          const res = await axios.get(`/api/brand-projects/${selectedBrand}/sensitive-words`);
          setWords(res.data.data || []);
        } catch(e) {
          console.error(e);
        } finally {
          setLoading(false);
        }
      }
      fetchWordsOnly();
    }
  }, [selectedBrand]);

  const handleSave = async (e) => {
    e.preventDefault();
    const projectId = selectedBrand;
    if (!projectId || projectId === 'all' || projectId === 'undefined') return alert('请先选择项目');

    if (!formData.word.trim()) return alert('请输入关键词');

    let newWords = [...words];
    if (editingWord !== null) {
      // Edit
      newWords = newWords.map((item, idx) => idx === editingWord ? { ...formData, word: formData.word.trim() } : item);
    } else {
      // Add
      if (newWords.some(w => w.word === formData.word.trim())) return alert('该关键词已存在');
      newWords.unshift({ ...formData, word: formData.word.trim(), id: Date.now().toString() });
    }

    try {
      await axios.put(`/api/brand-projects/${projectId}/sensitive-words`, { sensitiveWords: newWords });
      setWords(newWords);
      closeModal();
    } catch (error) {
      console.error(error);
      alert('保存失败');
    }
  };

  const handleDelete = async (index) => {
    if (!confirm('确定要删除该关键词吗？')) return;
    
    const projectId = selectedBrand;
    const newWords = words.filter((_, idx) => idx !== index);
    
    try {
      await axios.put(`/api/brand-projects/${projectId}/sensitive-words`, { sensitiveWords: newWords });
      setWords(newWords);
    } catch (error) {
      console.error(error);
      alert('删除失败');
    }
  };

  const toggleStatus = async (index, field) => {
    const projectId = selectedBrand;
    const newWords = words.map((item, idx) => {
      if (idx === index) return { ...item, [field]: !item[field] };
      return item;
    });

    try {
      await axios.put(`/api/brand-projects/${projectId}/sensitive-words`, { sensitiveWords: newWords });
      setWords(newWords);
    } catch (error) {
      console.error(error);
      alert('更新状态失败');
    }
  };

  const openModal = (word = null, index = null) => {
    if (word) {
      setFormData(word);
      setEditingWord(index);
    } else {
      setFormData({ word: '', category: '负面评价', enabled: true, llmCheck: true });
      setEditingWord(null);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({ word: '', category: '负面评价', enabled: true, llmCheck: true });
    setEditingWord(null);
  };

  const filteredWords = words.filter(w => w.word.includes(searchTerm) || w.category.includes(searchTerm));

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex justify-between items-center mb-6 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-red-500" />
            品牌敏感词库
          </h1>
          <div className="flex items-center gap-4 mt-4 bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700">
            <label className="text-sm font-semibold text-gray-700 dark:text-slate-300 whitespace-nowrap">
              当前配置项目：
            </label>
            <div className="relative flex-1 max-w-sm">
              <select 
                value={selectedBrand} 
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="w-full appearance-none bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-600 text-gray-900 dark:text-slate-100 text-sm font-medium rounded-lg focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 block pl-4 pr-10 py-2.5 transition-colors cursor-pointer outline-none"
              >
                {brands.length === 0 ? (
                  <option value="">暂无项目</option>
                ) : (
                  brands.map(b => (
                    <option key={b.id} value={b.id}>{b.name}{b.subBrandName ? ` - ${b.subBrandName}` : ''}</option>
                  ))
                )}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
              </div>
            </div>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-3 pl-1">
            设置该品牌的专属敏感词库。结合大模型实体剥离技术，精准拦截负面并防止“竞品躺枪”。
          </p>
        </div>
        <button 
          onClick={() => openModal()}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" /> 新增敏感词
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-6 flex-1 flex flex-col min-h-0">
        <div className="flex flex-wrap items-center gap-4 mb-6 shrink-0">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="搜索关键词或分类..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 dark:text-white transition-shadow"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="text-sm px-4 py-2 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400">
            词库总量: <span className="font-bold text-red-500">{words.length}</span> 个
          </div>
        </div>

        <div className="flex-1 overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-slate-900 border-b border-gray-100 dark:border-slate-700">
                <th className="p-4 font-semibold text-gray-600 dark:text-gray-300">关键词</th>
                <th className="p-4 font-semibold text-gray-600 dark:text-gray-300">所属分类</th>
                <th className="p-4 font-semibold text-gray-600 dark:text-gray-300 text-center">状态</th>
                <th className="p-4 font-semibold text-gray-600 dark:text-gray-300 text-center">大模型排误</th>
                <th className="p-4 font-semibold text-gray-600 dark:text-gray-300 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="p-8 text-center text-gray-500">加载中...</td></tr>
              ) : filteredWords.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-16 text-center text-gray-500">
                    <ShieldAlert className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p>暂无敏感词数据，请点击右上角新增。</p>
                  </td>
                </tr>
              ) : (
                filteredWords.map((item, index) => (
                  <tr key={index} className="border-b border-gray-50 dark:border-slate-700/50 hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors">
                    <td className="p-4">
                      <span className="font-bold text-red-600 dark:text-red-400">{item.word}</span>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 text-xs rounded-full">
                        {item.category}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <button 
                        onClick={() => toggleStatus(index, 'enabled')}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${item.enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'}`}
                      >
                        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${item.enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                      </button>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex justify-center items-center gap-2">
                        <button 
                          onClick={() => toggleStatus(index, 'llmCheck')}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${item.llmCheck ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-slate-600'}`}
                        >
                          <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${item.llmCheck ? 'translate-x-5' : 'translate-x-1'}`} />
                        </button>
                        {item.llmCheck && <Bot className="w-4 h-4 text-indigo-500" title="已开启大模型实体判定拦截" />}
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <button onClick={() => openModal(item, index)} className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors mr-2">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(index)} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center p-5 border-b border-gray-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {editingWord !== null ? '编辑敏感词' : '新增敏感词'}
              </h2>
            </div>
            <form onSubmit={handleSave} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">关键词</label>
                  <input 
                    type="text" 
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                    placeholder="如: 质量差"
                    value={formData.word}
                    onChange={e => setFormData({...formData, word: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">所属分类</label>
                  <div className="relative">
                    <select 
                      className="w-full appearance-none px-4 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-white transition-colors cursor-pointer outline-none"
                      value={formData.category}
                      onChange={e => setFormData({...formData, category: e.target.value})}
                    >
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800/50">
                  <div className="flex items-start gap-3">
                    <Bot className="w-5 h-5 text-indigo-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white text-sm">启用大模型排误分析</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">命中此词时，由AI判定是否为竞品躺枪，避免误杀。</div>
                    </div>
                  </div>
                  <button 
                    type="button"
                    onClick={() => setFormData({...formData, llmCheck: !formData.llmCheck})}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 ${formData.llmCheck ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-slate-600'}`}
                  >
                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.llmCheck ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>
              <div className="mt-8 flex justify-end gap-3">
                <button type="button" onClick={closeModal} className="px-4 py-2 text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-slate-700 rounded-lg">取消</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg shadow-sm">
                  {editingWord !== null ? '保存修改' : '确认添加'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
